import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Members sign-in page (v8.5 — email + password).
 *
 * Three modes:
 *   - 'signin'  — default. Email + password. On 200 → /members/library.
 *                 On 202 + status='needs_setup' → show "we emailed a
 *                 setup link" banner. On 401 → show error.
 *   - 'forgot'  — single email field, posts to /api/members-forgot-password.
 *                 Always shows the same generic confirmation regardless
 *                 of whether the email matched a member.
 *
 * Both modes render in the same layout so the page never feels like it
 * navigated.
 */
export default function Members() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const resetMessages = () => {
    setErrorMessage('');
    setInfoMessage('');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    resetMessages();
    try {
      const res = await fetch('/api/members-signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        navigate(data.redirect || '/members/library', { replace: true });
        return;
      }
      if (res.status === 202 && data.status === 'needs_setup') {
        setPassword('');
        setInfoMessage(
          data.message ||
            'Your account does not have a password yet. We emailed you a setup link.'
        );
        setSubmitting(false);
        return;
      }
      setErrorMessage(
        data.error ||
          'Could not sign you in. Please check your credentials and try again.'
      );
    } catch (_err) {
      setErrorMessage('Network error. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    resetMessages();
    try {
      const res = await fetch('/api/members-forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      setInfoMessage(
        data.message ||
          'If the email is associated with an active subscription, a password reset link has been sent.'
      );
    } catch (_err) {
      setErrorMessage('Network error. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  const switchToForgot = () => {
    resetMessages();
    setPassword('');
    setMode('forgot');
  };
  const switchToSignIn = () => {
    resetMessages();
    setMode('signin');
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={kickerStyle}>Members Area</div>

        <h1 style={headingStyle}>
          {mode === 'signin' ? 'Sign in' : 'Reset password'}
        </h1>

        <p style={subheadingStyle}>
          {mode === 'signin'
            ? 'Enter your subscription email and password to access the report archive.'
            : 'Enter the email associated with your subscription. We will send you a link to choose a new password.'}
        </p>

        {errorMessage && <div style={errorBoxStyle}>{errorMessage}</div>}
        {infoMessage && <div style={infoBoxStyle}>{infoMessage}</div>}

        {mode === 'signin' ? (
          <form onSubmit={handleSignIn} style={formStyle}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={submitting}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#4a9eff')}
              onBlur={(e) =>
                (e.target.style.borderColor = 'rgba(74, 158, 255, 0.25)')
              }
            />
            <div style={passwordWrapperStyle}>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 10 characters)"
                autoComplete="current-password"
                disabled={submitting}
                style={inputWithToggleStyle}
                onFocus={(e) => (e.target.style.borderColor = '#4a9eff')}
                onBlur={(e) =>
                  (e.target.style.borderColor = 'rgba(74, 158, 255, 0.25)')
                }
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={eyeButtonStyle}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            <button
              type="submit"
              disabled={submitting || !email.trim() || !password}
              style={primaryButtonStyle(submitting)}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={switchToForgot}
              disabled={submitting}
              style={linkButtonStyle}
            >
              Forgot password?
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgot} style={formStyle}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={submitting}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = '#4a9eff')}
              onBlur={(e) =>
                (e.target.style.borderColor = 'rgba(74, 158, 255, 0.25)')
              }
            />
            <button
              type="submit"
              disabled={submitting || !email.trim()}
              style={primaryButtonStyle(submitting)}
            >
              {submitting ? 'Sending…' : 'Email me a reset link'}
            </button>
            <button
              type="button"
              onClick={switchToSignIn}
              disabled={submitting}
              style={linkButtonStyle}
            >
              ← Back to sign in
            </button>
          </form>
        )}

        <p style={notMemberStyle}>
          Not a member yet?{' '}
          <Link to="/subscription" style={subscribeLinkStyle}>
            Subscribe
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ---------- icons ---------- */

function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/* ---------- styles ---------- */

const pageStyle = {
  minHeight: 'calc(100vh - 80px)',
  padding: '4rem 2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const cardStyle = {
  maxWidth: '520px',
  width: '100%',
  textAlign: 'center',
};
const kickerStyle = {
  fontSize: '0.85rem',
  color: '#4a9eff',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  marginBottom: '1rem',
  fontWeight: 600,
};
const headingStyle = {
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '1rem',
  lineHeight: 1.2,
};
const subheadingStyle = {
  color: '#a8c5e6',
  fontSize: '1.05rem',
  lineHeight: 1.7,
  marginBottom: '2rem',
};
const errorBoxStyle = {
  padding: '1rem 1.25rem',
  background: 'rgba(255, 80, 80, 0.08)',
  border: '1px solid rgba(255, 80, 80, 0.25)',
  borderRadius: '8px',
  color: '#ffb8b8',
  fontSize: '0.9rem',
  marginBottom: '1.25rem',
  textAlign: 'left',
  lineHeight: 1.5,
};
const infoBoxStyle = {
  padding: '1rem 1.25rem',
  background: 'rgba(80, 227, 194, 0.08)',
  border: '1px solid rgba(80, 227, 194, 0.25)',
  borderRadius: '8px',
  color: '#a8e6d4',
  fontSize: '0.95rem',
  marginBottom: '1.25rem',
  textAlign: 'left',
  lineHeight: 1.6,
};
const formStyle = { display: 'flex', flexDirection: 'column', gap: '1rem' };
const inputStyle = {
  width: '100%',
  padding: '1rem 1.25rem',
  fontSize: '1rem',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(74, 158, 255, 0.25)',
  borderRadius: '8px',
  color: '#ffffff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
};
const passwordWrapperStyle = { position: 'relative', width: '100%' };
const inputWithToggleStyle = {
  ...inputStyle,
  paddingRight: '3rem', // make room for the eye button
};
const eyeButtonStyle = {
  position: 'absolute',
  right: '0.85rem',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'transparent',
  border: 'none',
  color: '#6b9bc9',
  cursor: 'pointer',
  padding: '0.4rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const primaryButtonStyle = (submitting) => ({
  padding: '1.1rem 2rem',
  background: submitting
    ? 'rgba(74, 158, 255, 0.4)'
    : 'linear-gradient(135deg, #4a9eff 0%, #1a6ed8 100%)',
  border: 'none',
  borderRadius: '8px',
  color: '#ffffff',
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: submitting ? 'wait' : 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: submitting ? 'none' : '0 4px 20px rgba(74, 158, 255, 0.3)',
});
const linkButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#6b9bc9',
  fontSize: '0.9rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: '0.5rem',
  textDecoration: 'none',
};
const notMemberStyle = {
  marginTop: '2.5rem',
  color: '#6b9bc9',
  fontSize: '0.9rem',
};
const subscribeLinkStyle = {
  color: '#4a9eff',
  textDecoration: 'none',
  fontWeight: 600,
};
